import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import times from 'lodash/times';
import Styled from '~/components/Styled/Styled';
import CardHeader from '~/components/CardHeader/CardHeader';
import PlayerHeadshot from '~/components/PlayerHeadshot/PlayerHeadshot';
import css from '~/components/KeyPlayersCard/KeyPlayersCard.scss';
import getOr from '~/util/getOr';

const getTeamLogo = player =>
  get(player, 'team.logos.small', '') || get(player, 'teams[0].logos.small', '');
const getTeamName = player =>
  get(player, 'team.full_name', '') || get(player, 'teams[0].full_name', '');

const renderHeader = header => (
  <CardHeader>
    <div className={css.headerText}>{header}</div>
  </CardHeader>
);

const renderStats = (player, stats) =>
  stats.map(s => (
    <div className={css.playerStat} key={`${player.id}-${s.label}`}>
      <div className={css.statInfo}>{s.stat || '-'}</div>
      <div className={css.statLabel}>{s.label}</div>
    </div>
  ));

const renderStars = stars => (
  <div className={css.starsContainer}>
    <div className={css.stars}>
      {times(stars, i => (
        <img
          key={`star-${i}`}
          className={css.star}
          src="/static/vectors/star.svg"
          alt=""
          aria-hidden="true"
        />
      ))}
    </div>
  </div>
);

const RenderPreGamePitcherRecord = ({ statsRecord }) => {
  const wins = getOr(statsRecord, 'wins', '0');
  const losses = getOr(statsRecord, 'losses', '0');
  const earnedRunAverage = getOr(statsRecord, 'earned_run_average', '0.00');

  const pitcherRecord = (
    <span className={css.pitcherRecord}>
      ({wins}-{losses}, {earnedRunAverage})
    </span>
  );

  return pitcherRecord;
};

const RenderPostGamePitcherRecord = ({ statsRecord }) => {
  const losingPitcher = statsRecord.game_lost;
  const winningPitcher = statsRecord.game_won;
  const savingPitcher = statsRecord.game_saved;

  let pitcherRecord = <div />;

  if (winningPitcher) {
    pitcherRecord = (
      <span className={css.pitcherRecord}>{`(W, ${statsRecord.wins}-${statsRecord.losses}, ${
        statsRecord.earned_run_average
      })`}</span>
    );
  } else if (losingPitcher) {
    pitcherRecord = (
      <span className={css.pitcherRecord}>{`(L, ${statsRecord.wins}-${statsRecord.losses}, ${
        statsRecord.earned_run_average
      })`}</span>
    );
  } else if (savingPitcher) {
    pitcherRecord = <span className={css.pitcherRecord}>{`(S, ${statsRecord.saves})`}</span>;
  }

  return pitcherRecord;
};

const renderPlayer = (player, stats, statsRecord, stars = 0, sport, status) => (
  <div className={css.playerContainer} key={player.id}>
    <div className={css.playerRow}>
      <div className={css.playerInfo}>
        <div className={css.left}>
          <div className={css.columnContainer}>
            <img
              className={css.teamLogo}
              src={getTeamLogo(statsRecord)}
              alt={getTeamName(statsRecord)}
            />
            <div className={css.playerNumber}>{player.number}</div>
          </div>
        </div>
        <div className={css.imageContainer}>
          <PlayerHeadshot player={player} />
        </div>
        <div className={css.right}>
          <div className={css.columnContainer}>
            <div className={css.playerName}>
              <div className={css.pitcherRecordContainer}>
                <span className={css.pitcherName}>
                  {player.full_name}{' '}
                  {sport === 'baseball' && status === 'pre_game' ? (
                    <RenderPreGamePitcherRecord statsRecord={statsRecord} />
                  ) : null}
                </span>
                {sport === 'baseball' && status === 'final' ? (
                  <RenderPostGamePitcherRecord statsRecord={statsRecord} />
                ) : null}
              </div>
              {sport === 'soccer' && (
                <span className={css.playerPosition}>({player.position})</span>
              )}
            </div>
            <div className={css.playerStats}>{renderStats(player, stats)}</div>
          </div>
        </div>
      </div>
      {stars > 0 && renderStars(stars)}
    </div>
  </div>
);

const renderPlayers = (players, stats, statsRecords, stars = false, sport, status) =>
  players.map((player, i) =>
    renderPlayer(player, stats[i], statsRecords[i], stars ? i + 1 : 0, sport, status)
  );

// eslint-disable-next-line react/prefer-stateless-function
class KeyPlayersCard extends React.Component {
  /* prettier-ignore */
  render() {
    const { players, stats, statsRecords, playersByHeaders, title, starPlayers, sport, status } =
      this.props;

    if (
      isEmpty(playersByHeaders) &&
      players.every(e => !Object.keys(e) || Object.keys(e).length === 1)
    ) {
      return <div />;
    }

    if (isEmpty(playersByHeaders)) {
      const playersToRender = starPlayers ? players.slice(0, 3) : players;
      const winningPitcherID = get(playersToRender[0], 'id', '');
      const losingPitcherID = get(playersToRender[1], 'id', '');
      const statsToPass = sport === 'baseball' ? statsRecords : players

      if (winningPitcherID && losingPitcherID) {
        return (
          <div className={css.root}>
            <h2 className={css.title}>{title}</h2>
            <div>
              {renderPlayers(playersToRender, stats, statsToPass, starPlayers, sport, status)}
            </div>
          </div>
        );
      }

      return <div />;
    }

    return (
      <div className={css.root}>
        <h2 className={css.title}>{title}</h2>
        {Object.keys(playersByHeaders).map(header => {
          const playersAndStats = playersByHeaders[header];
          const playerList = playersAndStats.players;
          const statList = playersAndStats.stats;
          return (
            <div className={css.header} key={header}>
              {renderHeader(header)}
              <div>{renderPlayers(playerList, statList, playerList, false, sport)}</div>
            </div>
          );
        })}
      </div>
    );
  }
}

/*
  This class requires either players and stats or playersByHeaders.

  Players by headers should be a map with the keys being headers,
  and the values being an object with a list of players and a list of stats
  ex: playersByHeaders: { "Passing": {players: [], stats: [] } }
*/

const playersShape = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.number,
    full_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    headshots: PropTypes.object,
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    teams: PropTypes.arrayOf(
      PropTypes.shape({
        full_name: PropTypes.string,
        colour_1: PropTypes.string,
        logos: PropTypes.object,
      })
    ),
    position_abbreviation: PropTypes.string,
  })
);

KeyPlayersCard.propTypes = {
  players: playersShape,
  stats: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        stat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    )
  ),
  statsRecords: PropTypes.object,
  title: PropTypes.string.isRequired,
  starPlayers: PropTypes.bool,
  playersByHeaders: PropTypes.object,
  sport: PropTypes.string,
  status: PropTypes.string,
};

KeyPlayersCard.defaultProps = {
  players: [],
  stats: [],
  statsRecords: [],
  starPlayers: false,
  playersByHeaders: {},
  status: '',
  sport: null,
};

RenderPreGamePitcherRecord.propTypes = {
  statsRecords: PropTypes.object.isRequired,
};

RenderPreGamePitcherRecord.defaultProps = {
  statsRecords: {},
};

RenderPreGamePitcherRecord.displayName = 'PreGamePitcherRecord';
KeyPlayersCard.displayName = 'KeyPlayersCard';

export default Styled(KeyPlayersCard, [css]);
